import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'

import settings from '@/settings'
import { useAppStore } from '@/stores/app'
import { useCartStore } from '@/stores/cart'
import { useCartFlowStore } from '@/stores/cart-flow'
import { useSearchStore } from '@/stores/search'
import { useSessionStore } from '@/stores/session'

type State = {
  asmAgentId: string | null
  asmEmulatedUid: string | null
  isAsmSession: boolean
  isAsmPanelVisible: boolean
}

type Getters = {
  isAsmLoggedIn: (state: State) => boolean
  isAsmEmulation: (state: State) => boolean
}
type Actions = {
  asmLogin: (login: string, password: string) => Promise<void>
  asmLogout: () => Promise<void>
  startEmulation: (email: string) => Promise<void>
  stopEmulation: () => Promise<void>
  stopAsmMode: () => Promise<void>
  initAsmState: () => void
  toggleAsmPanel: (isAsmPanelVisible: boolean) => void
}

export const useAsmStore = defineStore<string, State, Getters, Actions>('asm', {
  state: () => ({
    asmAgentId: null,
    asmEmulatedUid: null,
    isAsmSession: false,
    isAsmPanelVisible: false,
  }),

  getters: {
    isAsmLoggedIn: (state) => !!state.asmAgentId,
    isAsmEmulation: (state) => !!state.asmEmulatedUid,
  },

  actions: {
    async asmLogin(login, password) {
      await api.asm.login(login, password)
      this.asmAgentId = login
      this.isAsmSession = true
    },

    async asmLogout() {
      const appStore = useAppStore()
      const sessionStore = useSessionStore()

      appStore.toggleLoader(true)
      await api.asm.logout()
      sessionStore.resetAuthWithoutLastRequest()
      this.$reset()
      await sessionStore.resetStateOnLoginChange()
      appStore.toggleLoader(false)
    },

    async startEmulation(email) {
      const appStore = useAppStore()
      const sessionStore = useSessionStore()
      const cartStore = useCartStore()
      const cartFlowStore = useCartFlowStore()
      const searchStore = useSearchStore()

      appStore.toggleLoader(true)
      await api.asm.emulate(email)
      await sessionStore.resetStateOnLoginChange()
      await sessionStore.updateUserData(email)
      await cartStore.getCart(true)

      if (cartStore.productsQty) {
        cartFlowStore.setStep('view')
      }

      await searchStore.setCompanyCatalogAvailability()
      await searchStore.setStandardCatalogAvailability()

      this.asmEmulatedUid = email
      appStore.toggleLoader(false)
    },

    async stopEmulation() {
      const appStore = useAppStore()
      const sessionStore = useSessionStore()

      try {
        appStore.toggleLoader(true)
        await api.asm.stopEmulation()
      } finally {
        this.asmEmulatedUid = null
        sessionStore.resetAuthWithoutLastRequest()
        await sessionStore.resetStateOnLoginChange()
        appStore.toggleLoader(false)
      }
    },

    async stopAsmMode() {
      const appStore = useAppStore()

      appStore.toggleLoader(true)
      await this.asmLogout()
      this.isAsmPanelVisible = false
      appStore.toggleLoader(false)
    },

    initAsmState() {
      const queryParams = new URLSearchParams(window.location.search)
      this.isAsmPanelVisible = !!queryParams.get(settings.services.asm.asmToken)
    },

    toggleAsmPanel(isAsmPanelVisible) {
      if (!isAsmPanelVisible) {
        const queryParams = new URLSearchParams(window.location.search)
        queryParams.delete(settings.services.asm.asmToken)
        const queryString = queryParams.toString() ? '?' + queryParams.toString() : ''
        history.replaceState(
          history.state,
          document.title,
          window.location.origin + window.location.pathname + queryString,
        )
      }
      this.isAsmPanelVisible = isAsmPanelVisible
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAsmStore, import.meta.hot))
}
